import React, { useState, useEffect, useContext, useMemo } from "react";
import { createPortal } from "react-dom";
import { X, Save, Layers } from "lucide-react";
import cadviewer from "cadviewer";
import { CadviewerContext } from "../../../context/Cadviewer.Context";
import { motion, useDragControls, useMotionValue } from "motion/react";

interface LayerSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (offLayers: string[]) => void;
  initialOffLayers: string[];
  viewName: string;
}

interface LayerItem {
  id: string;
  name: string;
  state: "On" | "Off";
  color?: string;
}

export const LayerSelectorModal: React.FC<LayerSelectorModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialOffLayers,
  viewName
}) => {
  const { currentCanvasInstance } = useContext(CadviewerContext);
  const [layers, setLayers] = useState<LayerItem[]>([]);

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const dragControls = useDragControls();

  const dragConstraints = useMemo(
    () => ({
      left: -(window.innerWidth - 100),
      right: window.innerWidth - 100,
      top: -(window.innerHeight - 100),
      bottom: window.innerHeight - 100,
    }),
    []
  );

  useEffect(() => {
    if (isOpen) {
      try {
        const layerTable = cadviewer.cvjs_getLayerTable(currentCanvasInstance || "floorPlan");
        
        if (layerTable && layerTable.layers) {
          console.log("=== LayerSelectorModal Debug ===");
          console.log("initialOffLayers:", initialOffLayers);
          const parsedLayers = layerTable.layers.map((l: any) => {
            const id = l.layer || l.name || l.layerName || "";
            // Determine state: if it's in initialOffLayers (case-insensitive and trimmed), it's Off.
            const isOff = initialOffLayers.some((offLayer) => {
              if (!offLayer) return false;
              const offLayerLower = String(offLayer).trim().toLowerCase();
              const match = offLayerLower === String(id).trim().toLowerCase() ||
                            offLayerLower === String(l.layerName || "").trim().toLowerCase() ||
                            offLayerLower === String(l.name || "").trim().toLowerCase() ||
                            offLayerLower === String(l.layer || "").trim().toLowerCase();
              if (match) console.log("Matched layer off:", id);
              return match;
            });
            
            // Visual preview: apply state to CADViewer
            if (isOff) {
              cadviewer.cvjs_LayerOff(id);
            } else {
              cadviewer.cvjs_LayerOn(id);
            }

            return {
              id,
              name: l.layerName || l.layer || l.name,
              state: isOff ? "Off" : "On",
              color: l.colorHex || l.color || "#ccc",
            };
          });
          setLayers(parsedLayers);
        }
      } catch (e) {
        console.error("Error fetching layer table:", e);
      }
    }
  }, [isOpen, currentCanvasInstance, initialOffLayers]);

  // Reset position on open
  useEffect(() => {
    if (isOpen) {
      x.set(0);
      y.set(0);
    }
  }, [isOpen, x, y]);

  if (!isOpen) return null;

  const handleToggleLayer = (layerId: string, currentState: "On" | "Off") => {
    const newState = currentState === "On" ? "Off" : "On";
    
    if (newState === "On") {
      cadviewer.cvjs_LayerOn(layerId);
    } else {
      cadviewer.cvjs_LayerOff(layerId);
    }

    setLayers((prev) =>
      prev.map((l) => (l.id === layerId ? { ...l, state: newState } : l))
    );
  };

  const handleAllLayersOn = () => {
    layers.forEach((l) => {
      if (l.state === "Off") {
        cadviewer.cvjs_LayerOn(l.id);
      }
    });
    setLayers((prev) => prev.map((l) => ({ ...l, state: "On" })));
  };

  const handleAllLayersOff = () => {
    layers.forEach((l) => {
      if (l.state === "On") {
        cadviewer.cvjs_LayerOff(l.id);
      }
    });
    setLayers((prev) => prev.map((l) => ({ ...l, state: "Off" })));
  };

  const handleSave = () => {
    const offLayers = layers.filter(l => l.state === "Off").map(l => l.id);
    onSave(offLayers);
  };

  return createPortal(
    <div className="fixed inset-0 z-[1000000010] flex items-center justify-center pointer-events-none p-4 sm:p-0">
      <motion.div 
        drag
        dragControls={dragControls}
        dragListener={false}
        dragConstraints={dragConstraints}
        dragElastic={0}
        dragMomentum={false}
        style={{ x, y }}
        className="bg-white rounded-lg shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden relative pointer-events-auto"
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between px-4 py-3 bg-[#0f3557] text-white cursor-move"
          onPointerDown={(e) => dragControls.start(e)}
        >
          <div className="flex items-center gap-2">
            <Layers className="w-5 h-5" />
            <h2 className="text-sm font-semibold pointer-events-none">
              Set Layers for: {viewName}
            </h2>
          </div>
          <button onClick={onClose} className="hover:bg-[#1a4b77] p-1 rounded transition" onPointerDown={(e) => e.stopPropagation()}>
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {layers.map((layer) => {
            const isOn = layer.state === "On";
            return (
              <div
                key={layer.id}
                className={`flex items-center justify-between rounded-md px-2 py-1 transition-all ${
                  isOn ? "bg-amber-300/80" : "bg-slate-100"
                } hover:brightness-95`}
              >
                <div className="flex items-center gap-2 overflow-hidden">
                  <span className="text-slate-500 opacity-60 flex-shrink-0 text-xs">☰</span>
                  <div
                    className="h-3 w-3 rounded-sm border border-gray-400 flex-shrink-0"
                    style={{ backgroundColor: layer.color }}
                  />
                  <span className="text-[11px] font-semibold tracking-tight text-slate-800 truncate" title={layer.name}>
                    {layer.name}
                  </span>
                </div>
                <button
                  onClick={() => handleToggleLayer(layer.id, layer.state)}
                  className={`relative inline-flex flex-shrink-0 h-4 w-7 items-center rounded-full transition-colors duration-200 ease-in-out border border-transparent ${
                    isOn ? "bg-emerald-400 shadow-sm" : "bg-slate-300"
                  }`}
                  title={`Toggle ${layer.name}`}
                >
                  <span className="sr-only">Toggle layer</span>
                  <span
                    className={`inline-block h-3 w-3 transform rounded-full bg-white transition duration-200 ease-in-out shadow-sm ${
                      isOn ? "translate-x-[14px]" : "translate-x-0.5"
                    }`}
                  />
                </button>
              </div>
            );
          })}
          {layers.length === 0 && (
            <div className="text-center text-gray-500 py-8 text-sm">
              No layers found. Make sure a drawing is loaded.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-3 bg-slate-50 flex justify-between items-center">
          <div className="flex gap-2">
            <button
              onClick={handleAllLayersOn}
              className="px-3 py-1.5 text-xs font-medium border border-slate-300 rounded hover:bg-slate-100 text-slate-700"
            >
              All On
            </button>
            <button
              onClick={handleAllLayersOff}
              className="px-3 py-1.5 text-xs font-medium border border-slate-300 rounded hover:bg-slate-100 text-slate-700"
            >
              All Off
            </button>
          </div>
          <button
            onClick={handleSave}
            className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-4 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-blue-500"
          >
            <Save className="h-3.5 w-3.5" />
            Save
          </button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
};
